import { LOCALE_ID, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';

import { AppComponent } from './app.component';
import { SocketIoModule, SocketIoConfig } from 'ngx-socket-io';
import { LOCAL_STORAGE_CONFIG } from './core/services/storage.tokens';

// Interceptors
import { ErrorHandlingInterceptor } from './interceptors/error-handling.interceptor';
import { WhiteLabelInterceptor } from './interceptors/whitelabel.interceptor';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { ShiftDatesInterceptor } from './interceptors/shift-dates.interceptor';
import { SeoParamsInterceptor } from './interceptors/seo-params.interceptor';
import { FileTransferInterceptor } from './interceptors/file-transfer.interceptor';
import { SeoHeadersInterceptor } from './interceptors/seo-headers.interceptor';
import { BaseUrlInterceptor } from './interceptors/base-url.interceptor';
import { AdsParamsInterceptor } from './interceptors/ads-params.interceptor';
import { WcagInterceptor } from './interceptors/wcag.interceptor';
import { SupportActivityInterceptor } from './support-system/services/support-activity.interceptor';

// Guards
import { AuthcheckerGuard } from './authentication/guard/authchecker.guard';
import { MasterGuardService } from './authentication/guard/masterchecker.guard';

// Components
import { OrganicLoginComponent } from './session/organic/login/login.component';
import { SignupOrganicComponent } from './session/organic/signup/signup.component';
import { SignupPremiumComponent } from './session/organic/signup-premium/signup-premium.component';
import { PhoneNumberComponent } from './session/organic/phone-number/phone-number.component';
import { LoginComponent } from './session/login/login.component';
import { ResetpasswordComponent } from './session/resetpassword/resetpassword.component';
import { SetpasswordComponent } from './session/setpassword/setpassword.component';
import { SidenavComponent } from './sidenav/sidenav.component';
import { BackendMetricsComponent } from './metrics/backend-metrics/backend-metrics.component';
import { DownloadServiceWorkerComponent } from './seo/download-service-worker/download-service-worker.component';
import { UserPermissionsComponent } from './user-permissions/user-permissions.component';
import { UserPermissionsActionComponent } from './user-permissions/modals/user-action/user-action.component';
import { OrganicPaymentConfigComponent } from './user-permissions/modals/organic-payment-config/organic-payment-config.component';
import { SharedModule } from './shared/shared.module';
import { UnderMaintenanceComponent } from './banners/under-maintenance/under-maintenance.component';
import { PaymentConfirmedComponent } from './invoices/payment-confirmed/payment-confirmed.component';
import { AiChatComponent } from './recordings/ai-chat/ai-chat.component';
import { KeywordLimitComponent } from './modals/keyword-limit/keyword-limit.component';
import { DateRangePickerComponent } from './modals/date-range-picker/date-range-picker.component';
import { ShopifyErrorComponent } from './utils/shopify-error/shopify-error.component';

// Translate module
import {
  TranslateModule,
  TranslateLoader,
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateService,
} from '@ngx-translate/core';
import { BillingFullscreenComponent } from './seo/billing-fullscreen/billing-fullscreen.component';
import { ResilientOverlayContainer } from './utils/resilient-overlay-container';
import { OverlayContainer } from '@angular/cdk/overlay';

// standalone components
import { FullScreenPreviewComponent } from './utils/full-screen-preview/full-screen-preview.component';
import { SupportChatBoxComponent } from './support-system/chat-box/chat-box.component';
import { provideSupportSystem } from './support-system/support-system.providers';

// Locale data
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import localeJp from '@angular/common/locales/ja';
import localeIt from '@angular/common/locales/it';
import { LangParamsInterceptor } from './interceptors/lang-params.interceptor';
import { HashedTranslateLoader } from './core/translate/hashed-translation-loader';
import { DecodeTokenComponent } from './shared/decode-token/decode-token.component';

registerLocaleData(localeEn);
registerLocaleData(localeDe);
registerLocaleData(localeFr);
registerLocaleData(localeJp);
registerLocaleData(localeIt);

export class CustomMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    // console.warn(`Missing translation for: ${params.key}`);
    return params.key;
  }
}

const config: SocketIoConfig = {
  url: 'https://automation.behamics.com',
  options: {
    extraHeaders: {
      'X-SOCKETIO-AUTH': 'behamics-socketio',
    },
    path: '/socket.io',
  },
};

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    SignupOrganicComponent,
    SignupPremiumComponent,
    PhoneNumberComponent,
    OrganicLoginComponent,
    ResetpasswordComponent,
    SetpasswordComponent,
    SidenavComponent,
    BackendMetricsComponent,
    DownloadServiceWorkerComponent,
    UserPermissionsComponent,
    UserPermissionsActionComponent,
    OrganicPaymentConfigComponent,
    UnderMaintenanceComponent,
    PaymentConfirmedComponent,
    BillingFullscreenComponent,
    AiChatComponent,
    KeywordLimitComponent,
    DateRangePickerComponent,
    ShopifyErrorComponent,
    DecodeTokenComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SocketIoModule.forRoot(config),
    SharedModule,
    FullScreenPreviewComponent,
    SupportChatBoxComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => new HashedTranslateLoader(http),
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CustomMissingTranslationHandler,
      },
    }),
  ],
  schemas: [NO_ERRORS_SCHEMA],
  providers: [
    provideHttpClient(
      withInterceptors([
        WhiteLabelInterceptor,
        ShiftDatesInterceptor,
        WcagInterceptor,
        BaseUrlInterceptor,
        LangParamsInterceptor,
        SeoParamsInterceptor,
        AdsParamsInterceptor,
        FileTransferInterceptor,
        SeoHeadersInterceptor,
        AuthInterceptor,
        ErrorHandlingInterceptor,
        SupportActivityInterceptor,
      ]),
    ),
    provideSupportSystem(),
    AuthcheckerGuard,
    MasterGuardService,
    { provide: OverlayContainer, useClass: ResilientOverlayContainer },
    {
      provide: LOCAL_STORAGE_CONFIG,
      useValue: {
        namespace: 'bhm-dashboard',
        version: '3.0.2',
        preserveOnVersionChange: [
          'email',
          'auth_key',
          'darkMode',
          'language',
          'seenTours',
          'favoriteClientsSidenav',
        ],
        storage: 'local',
      },
    },
    {
      provide: LOCALE_ID,
      deps: [TranslateService],
      useFactory: (translate: TranslateService) =>
        translate.currentLang || 'en',
    },
  ],
  bootstrap: [AppComponent],
  exports: [],
})
export class AppModule {}
